import { Dropdown as SemanticDropdown, type DropdownItemProps as SemanticDropdownItemProps, type DropdownProps as SemanticDropdownProps } from "semantic-ui-react";

export interface DropdownItemProps<T extends string | number | boolean = number> extends Omit<SemanticDropdownItemProps, "key" | "value"> {
  key: string | number;
  value: T;
}

interface SingleDropdownProps<T extends string | number | boolean> extends Omit<SemanticDropdownProps, "options" | "multiple" | "onChange" | "value"> {
  options?: DropdownItemProps<T>[];
  multiple?: false;
  onChange?: (value?: T) => void;
  value?: T;
}

interface MultipleDropdownProps<T extends string | number | boolean> extends Omit<SemanticDropdownProps, "options" | "multiple" | "onChange" | "value"> {
  options?: DropdownItemProps<T>[];
  multiple: true;
  onChange?: (value: T[]) => void;
  value?: T[];
}

export type DropdownProps =
  | SingleDropdownProps<string>
  | SingleDropdownProps<number>
  | SingleDropdownProps<boolean>
  | MultipleDropdownProps<string>
  | MultipleDropdownProps<number>
  | MultipleDropdownProps<boolean>;

const isMultiple = (props: DropdownProps): props is MultipleDropdownProps<string> | MultipleDropdownProps<number> | MultipleDropdownProps<boolean> => {
  return !!props.multiple;
};

const isString = (props: DropdownProps): props is SingleDropdownProps<string> | MultipleDropdownProps<string> => {
  return typeof props.options?.at(0)?.value === "string";
};

const isBoolean = (props: DropdownProps): props is SingleDropdownProps<boolean> | MultipleDropdownProps<boolean> => {
  return typeof props.options?.at(0)?.value === "boolean";
};

export function Dropdown(props: SingleDropdownProps<string>): React.ReactNode;
export function Dropdown(props: SingleDropdownProps<number>): React.ReactNode;
export function Dropdown(props: SingleDropdownProps<boolean>): React.ReactNode;
export function Dropdown(props: MultipleDropdownProps<string>): React.ReactNode;
export function Dropdown(props: MultipleDropdownProps<number>): React.ReactNode;
export function Dropdown(props: MultipleDropdownProps<boolean>): React.ReactNode;
export function Dropdown(props: DropdownProps) {
  const { options, ...others } = props;

  const onSemanticChange = (_: unknown, data: SemanticDropdownProps) => {
    if (!props.onChange || !options?.length) return;
    if (isMultiple(props)) {
      if (data.value === undefined) return props.onChange([]);
      if (!Array.isArray(data.value)) data.value = [data.value];
      if (isString(props)) return props.onChange(data.value.filter(v => typeof v === "string"));
      if (isBoolean(props)) return props.onChange(data.value.filter(v => typeof v === "boolean"));

      return props.onChange(data.value.filter(v => typeof v === "number"));
    } else {
      if (Array.isArray(data.value)) data.value = data.value.length ? data.value[0] : undefined;
      if (data.value === undefined || data.value === "") return props.onChange(undefined);
      if (isString(props)) {
        if (typeof data.value === "string") return props.onChange(data.value);
      } else if (isBoolean(props)) {
        if (typeof data.value === "boolean") return props.onChange(data.value);
      } else if (typeof data.value === "number") return props.onChange(data.value);
    }
  };

  const semanticValue = others.clearable && others.value === undefined ? "" : others.value;
  return <SemanticDropdown {...others} options={options} onChange={onSemanticChange} value={semanticValue} />;
}
